import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { getApiBaseUrl } from '../utils/vanity-url.util';
import { SubscriptionModal } from './subscription.service';

export interface GroupSubscriptionPlanResponse {
  id: string;
  name: string;
  description: string;
  stripePricingLink: string;
  stripePaymentLink?: string;
  seatCount: number;
  durationMonths: number;
  price: number | null;
  currency: string;
  isPublished: boolean;
  createdAtUtc: string;
  updatedAtUtc?: string;
  benefits?: { icon: string; description: string }[];
  inUse?: boolean;
}

export interface CreateGroupSubscriptionRequest {
  name: string;
  description: string;
  stripePricingLink: string;
  stripePaymentLink?: string;
  seatCount?: number;
  durationMonths?: number;
  isPublished: boolean;
  benefits?: { icon: string; description: string }[];
}

@Injectable({
  providedIn: 'root',
})
export class GroupSubscriptionService {
  private apiUrl = getApiBaseUrl();

  constructor(private http: HttpClient) {}

  getGroupSubscriptions(): Observable<GroupSubscriptionPlanResponse[]> {
    return this.http.get<GroupSubscriptionPlanResponse[]>(`${this.apiUrl}/group-subscriptions`);
  }

  getGroupSubscriptionById(id: string): Observable<GroupSubscriptionPlanResponse> {
    return this.http.get<GroupSubscriptionPlanResponse>(`${this.apiUrl}/group-subscriptions/${id}`);
  }

  createGroupSubscription(subscriptionData: CreateGroupSubscriptionRequest): Observable<GroupSubscriptionPlanResponse> {
    return this.http.post<GroupSubscriptionPlanResponse>(`${this.apiUrl}/group-subscriptions`, subscriptionData);
  }

  deleteGroupSubscription(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/group-subscriptions/${id}`);
  }

  activateGroupSubscription(id: string): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/group-subscriptions/activate/${id}`, {});
  }

  editGroupSubscription(id: string, data: CreateGroupSubscriptionRequest): Observable<GroupSubscriptionPlanResponse> {
    return this.http.put<GroupSubscriptionPlanResponse>(`${this.apiUrl}/group-subscriptions/${id}`, data);
  }

  /**
   * Converts a GroupSubscriptionPlanResponse to SubscriptionModal format for UI compatibility
   */
  toSubscriptionModal(plan: GroupSubscriptionPlanResponse): SubscriptionModal {
    return {
      subscriptionId: plan.id,
      name: plan.name,
      description: plan.description || '',
      stripePricingLink: plan.stripePricingLink,
      stripePaymentLink: plan.stripePaymentLink || '',
      price: plan.price || 0,
      billingCycle: 'B2B',
      isPublished: plan.isPublished,
      createdAt: plan.createdAtUtc,
      planType: 'b2b',
      seatsCount: plan.seatCount,
      benefits: plan.benefits ?? [],
      inUse: plan.inUse ?? false,
    };
  }
}
